import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { WeatherWidget } from '@/components/dashboard/WeatherWidget';
import { FarmStats } from '@/components/dashboard/FarmStats';
import { PondGrid } from '@/components/dashboard/PondGrid';
import { FarmMapView } from '@/components/dashboard/FarmMapView';
import { AlertsSummary } from '@/components/dashboard/AlertsSummary';
import { FinancialSummaryWidget } from '@/components/financials/FinancialSummaryWidget';
import { LowStockWidget } from '@/components/inventory/LowStockWidget';
import { PondQuickViewModal } from '@/components/pond/PondQuickViewModal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Card } from '@/components/ui/Card';
import { NoFarmsEmptyState } from '@/components/farm';
import { GuidedTour } from '@/components/onboarding';
import { RefreshCw, AlertCircle, Map, LayoutDashboard, Waves, Clock } from 'lucide-react';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { useFarmTimezone } from '@/hooks/useFarmTimezone';
import { useMembership } from '@/hooks/useMembership';
import { farmStamp } from '@/lib/farmTime';
import { cn } from '@/lib/cn';
import type { FarmOverviewResponse, PondOverview, ApiResponse } from '@/types/api';

type ViewMode = 'map' | 'dashboard';

interface OverviewSearchParams {
  view?: ViewMode;
}

export const Route = createFileRoute('/_app/overview')({
  validateSearch: (search: Record<string, unknown>): OverviewSearchParams => ({
    view: search.view === 'dashboard' ? 'dashboard' : undefined,
  }),
  component: OverviewPage,
});

function OverviewPage() {
  const { farmId, farms, isLoading: isFarmLoading } = useCurrentFarm();
  const { isSupervisorOrAbove, isOperator, isLoading: isMembershipLoading } = useMembership();
  const { view: searchView } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [selectedPond, setSelectedPond] = useState<PondOverview | null>(null);

  const viewMode: ViewMode = searchView || 'map';

  const {
    data,
    isLoading,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery<ApiResponse<FarmOverviewResponse>>({
    queryKey: ['farm', 'overview', farmId],
    queryFn: () => api.get(`/api/v1/farms/${farmId}/overview`),
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
    enabled: !!farmId,
  });

  const farm = data?.data;
  const { timezone, settled: isTimezoneSettled } = useFarmTimezone(farmId);

  // No farms at all (fresh operator, or unassigned member): show a clean empty
  // state instead of the "Failed to load farm data" error / infinite spinner.
  if (!isFarmLoading && farms.length === 0) {
    return <NoFarmsEmptyState isOperator={isOperator} />;
  }

  if (isFarmLoading || isLoading || (!farmId && !error)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading farm data...</p>
      </div>
    );
  }

  if (error || !farm) {
    return (
      <Card className="border-status-critical/30">
        <Card.Body>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-status-critical" />
            </div>
            <h3 className="font-semibold text-gray-900">Failed to load farm data</h3>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              {error instanceof Error ? error.message : 'Please try again'}
            </p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const stamp = farmStamp(new Date(dataUpdatedAt), timezone);

  return (
    <div className="space-y-6">
      {/* Guided Tour for new users */}
      <GuidedTour />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Farm Overview</h1>
            <p className="text-gray-500">{farm.name}</p>
          </div>
          {isTimezoneSettled && (
            <span
              data-overview-stamp
              title="Data last refreshed"
              className="inline-flex max-w-full flex-wrap items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-base font-semibold text-gray-900"
            >
              <Clock className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
              <span data-overview-stamp-text>
                {stamp.date} · {stamp.time}
              </span>{' '}
              <span data-overview-stamp-zone className="text-sm font-medium text-gray-500">
                {stamp.label}
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => navigate({ search: { view: undefined } })}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                viewMode === 'map'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              <Map className="h-3.5 w-3.5" />
              Ponds
            </button>
            <button
              onClick={() => navigate({ search: { view: 'dashboard' } })}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                viewMode === 'dashboard'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
              Dashboard
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4" />
          </div>
        </div>
      </div>

      {farm.ponds.length === 0 ? (
        <>
          <NoPondsEmptyState isLoading={isMembershipLoading} canAddPonds={isSupervisorOrAbove} />
          <WeatherWidget farmId={farm.id} />
        </>
      ) : viewMode === 'map' ? (
        /* Farm Map View */
        <>
          <FarmMapView
            ponds={farm.ponds}
            alertThresholds={farm.alert_thresholds}
            onPondClick={setSelectedPond}
          />

          {/* Alerts Summary - only show if there are alerts */}
          {farm.pending_alert_count > 0 && (
            <AlertsSummary alerts={farm.recent_alerts} />
          )}

          {/* Weather Widget */}
          <WeatherWidget farmId={farm.id} />
        </>
      ) : (
        /* Dashboard View (original layout) */
        <>
          {/* Weather Widget */}
          <WeatherWidget farmId={farm.id} />

          {/* Stats Row */}
          <div data-tour="alerts-badge">
            <FarmStats
              totalPonds={farm.ponds.length}
              activeCycles={farm.active_cycle_count}
              pendingAlerts={farm.pending_alert_count}
              readingsToday={farm.readings_today_count}
            />
          </div>

          {/* Alerts Summary - only show if there are alerts */}
          {farm.pending_alert_count > 0 && (
            <AlertsSummary alerts={farm.recent_alerts} />
          )}

          {/* Financial & Inventory Widgets (supervisor+ only) */}
          {isSupervisorOrAbove && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <FinancialSummaryWidget farmId={farm.id} />
              {farm.inventory_tracking_enabled && (
                <LowStockWidget farmId={farm.id} />
              )}
            </div>
          )}

          {/* Pond Grid */}
          <section data-tour="pond-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Ponds</h2>
              <span className="text-sm text-gray-500">
                {farm.ponds.filter((p) => p.status === 'active').length} active
              </span>
            </div>
            <PondGrid ponds={farm.ponds} alertThresholds={farm.alert_thresholds} onPondClick={setSelectedPond} />
          </section>
        </>
      )}

      {/* Quick-View Popup */}
      <PondQuickViewModal
        pond={selectedPond}
        open={selectedPond !== null}
        onClose={() => setSelectedPond(null)}
        alertThresholds={farm.alert_thresholds}
      />
    </div>
  );
}

function NoPondsEmptyState({ isLoading, canAddPonds }: { isLoading: boolean; canAddPonds: boolean }) {
  return (
    <Card>
      <Card.Body>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center mb-4">
            <Waves className="h-7 w-7 text-primary-600" />
          </div>
          {isLoading ? (
            <LoadingSpinner size={24} className="text-primary-600" />
          ) : canAddPonds ? (
            <>
              <h3 className="text-lg font-semibold text-gray-900">Add your first pond</h3>
              <p className="text-sm text-gray-500 mt-1 mb-6 max-w-sm">
                Create the farm's ponds on the Ponds page to start logging water, feeding and cycles.
              </p>
              <Link
                to="/ponds"
                className="inline-flex min-h-11 items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Go to Ponds
              </Link>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900">No ponds yet</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-sm">
                Ask a Farm Technician to add the farm's ponds.
              </p>
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
